import { useState, useEffect, useRef, useMemo, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { SUBSCRIPTION_STATUS_MAP, TENANT_STATUS_MAP } from '@goyone/shared';
import { api } from '../../lib/api';
import { date, money, plural, WORDS } from '../../lib/format';
import { useAuth } from '../../auth/AuthProvider';
import { Icon } from '../../components/Icon';
import { Logo } from '../../components/Logo';
import { Badge, ErrorState, Modal, PageHead } from '../../components/ui';
import { useToast } from '../../components/Toast';
import QRCode from 'qrcode';
import { COUNTRY_NAMES, govsOf, citiesOf, govLabelOf } from '../../lib/geo';
import { downscaleFile } from '../../lib/imageResize';

const CYCLE_LABELS: Record<string, string> = { MONTHLY: 'شهريًّا', YEARLY: 'سنويًّا' };

const clampPct = (n: number): number => Math.max(0, Math.min(100, Math.round(n)));

/* أسماءٌ عربيّةٌ لرموز المزايا — يُعرض الرمزُ كما هو إن لم يُعرف. */
const FEATURE_LABELS: Record<string, string> = {
  crm: 'إدارة العملاء',
  sales: 'المبيعات والعقود',
  agency: 'إدارة المشاريع',
  finance: 'الحسابات والفواتير',
  hr: 'الموارد البشرية',
  reports: 'التقارير ولوحات البيانات',
  callcenter: 'مركز الاتصال',
  monitoring: 'مراقبة الحساب',
  desktop: 'تطبيق سطح المكتب',
  whatsapp: 'واتساب والمجيب الآلي',
};

const featureLabel = (code: string): string => FEATURE_LABELS[code] ?? code;

const INVOICE_STATUS: Record<string, { label: string; kind: string }> = {
  open: { label: 'مستحقّة', kind: 'warning' },
  paid: { label: 'مدفوعة', kind: 'success' },
  void: { label: 'ملغاة', kind: 'neutral' },
};

interface Billing {
  tenant: { status: string; trialEndsAt: string | null; createdAt: string };
  subscription: {
    status: string;
    cycle: string;
    price: number;
    currency: string;
    currentPeriodStart: string;
    currentPeriodEnd: string;
    graceEndsAt: string | null;
    cancelAtPeriodEnd: boolean;
    plan: {
      code: string;
      name: string;
      description: string;
      features: string[];
      limits: Record<string, number>;
    };
  } | null;
  seats: { used: number; limit: number };
  invoices: Array<{
    id: string;
    number: string;
    total: number;
    currency: string;
    issuedAt: string;
    dueAt: string;
    paidAt: string | null;
    status: string;
  }>;
  cards: CardRow[];
  billingInfo: BillingInfo;
  transfers: TransferRow[];
}

interface TransferRow {
  id: string;
  method: string;
  amount: number;
  currency: string;
  reference: string | null;
  status: string;
  reviewNote: string | null;
  createdAt: string;
  reviewedAt: string | null;
}

interface CardRow {
  id: string;
  brand: string | null;
  last4: string;
  expMonth: number | null;
  expYear: number | null;
  holder: string | null;
  isDefault: boolean;
}

interface BillingInfo {
  firstName?: string;
  lastName?: string;
  company?: string;
  country?: string;
  governorate?: string;
  city?: string;
  street?: string;
  buildingNo?: string;
  floor?: string;
  apartment?: string;
  postalCode?: string;
  taxNumber?: string;
  /** تفضيلُ الدفعِ بالبطاقة: تلقائيٌّ عند الاستحقاقِ أو يدويّ. */
  autopay?: boolean;
}

type InvoiceRow = Billing['invoices'][number];

/**
 * الاشتراكُ والدفع — صفحةٌ مستقلّةٌ لا بطاقةٌ في الملفّ.
 *
 * الباقةُ وفترتُها ومقاعدُها وفواتيرُ اشتراكها في مكانٍ واحدٍ يُفتح عند
 * الحاجة، منفصلٌ عن بيانات صاحب الحساب. للمدير وما فوق: الموظّفُ لا يعنيه
 * ما تدفعه الشركة.
 */
export function SubscriptionPage() {
  const { session } = useAuth();
  const qc = useQueryClient();
  const toast = useToast();
  const tenantName = session?.tenant.name ?? 'شركتك';
  const [preview, setPreview] = useState<string | null>(null);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const billing = useQuery({
    queryKey: ['settings', 'billing'],
    queryFn: () => api<Billing>('/settings/billing'),
  });

  const data = billing.data;
  const sub = data?.subscription ?? null;
  const invCtx = {
    planName: sub?.plan.name ?? 'اشتراك GoyOne',
    cycle: sub ? (CYCLE_LABELS[sub.cycle] ?? sub.cycle) : '',
    customer: tenantName,
  };

  const cancelMut = useMutation({
    mutationFn: (c: boolean) =>
      api('/settings/billing/cancel', { method: 'PATCH', body: { cancel: c } }),
    onSuccess: (_d, c) => {
      void qc.invalidateQueries({ queryKey: ['settings', 'billing'] });
      setConfirmCancel(false);
      toast(c ? 'أُلغي التجديد — يبقى وصولك حتى نهاية المدّة' : 'استُؤنف التجديد', 'success');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر التنفيذ'),
  });
  const cancelAtPeriodEnd = sub?.cancelAtPeriodEnd ?? false;
  const tenantStatus = data
    ? TENANT_STATUS_MAP[data.tenant.status as keyof typeof TENANT_STATUS_MAP]
    : null;
  const subStatus = sub
    ? SUBSCRIPTION_STATUS_MAP[sub.status as keyof typeof SUBSCRIPTION_STATUS_MAP]
    : null;

  const endsAt = sub?.currentPeriodEnd ?? data?.tenant.trialEndsAt ?? null;
  const startsAt = sub?.currentPeriodStart ?? data?.tenant.createdAt ?? null;
  const daysLeft = endsAt
    ? Math.ceil((new Date(endsAt).getTime() - Date.now()) / 86_400_000)
    : null;

  /* نسبةُ ما مضى من الفترة الحالية — للعدّاد المرئيّ. */
  const periodPct =
    startsAt && endsAt
      ? clampPct(
          ((Date.now() - new Date(startsAt).getTime()) /
            (new Date(endsAt).getTime() - new Date(startsAt).getTime())) *
            100,
        )
      : 0;

  /* نسبةُ إشغال المقاعد — بلا حدٍّ تُعرض ممتلئةً بلطف. */
  const seatsPct =
    data && data.seats.limit > 0
      ? clampPct((data.seats.used / data.seats.limit) * 100)
      : data && data.seats.limit === -1
        ? 12
        : 0;

  return (
    <div className="page">
      <PageHead
        title="الاشتراك والدفع"
        subtitle="باقتك وفترتها ومقاعدها وفواتير اشتراكك"
        actions={
          <Link to="/account" className="btn">
            <Icon name="user" size={15} />
            الملف الشخصي
          </Link>
        }
      />

      {billing.isError && (
        <ErrorState error={billing.error} onRetry={() => void billing.refetch()} />
      )}
      {billing.isPending && <div className="skeleton" style={{ height: 160 }} />}

      {data && (
        <>
          <h2 className="acc-sec-title">
            <span className="acc-sec-ic">
              <Icon name="diamond" size={17} />
            </span>
            الخطة
          </h2>

          {/* بطاقةُ الباقة — واجهةٌ متدرّجةٌ بالسعر والحالة */}
          <div className="subx-hero">
            <div className="subx-hero-top">
              <div className="row" style={{ gap: 'var(--sp-3)', alignItems: 'center' }}>
                <span className="subx-badge">
                  <Icon name="diamond" size={22} />
                </span>
                <div>
                  <h2 className="subx-name">{sub?.plan.name ?? 'الباقة التجريبية'}</h2>
                  <div className="subx-cycle">
                    {sub
                      ? `دورة ${CYCLE_LABELS[sub.cycle] ?? sub.cycle}`
                      : 'فترةُ تجربةٍ مجانية قبل الاشتراك'}
                  </div>
                </div>
              </div>
              <span className="subx-status">
                <Icon name="check" size={13} />
                {subStatus?.label ?? tenantStatus?.label ?? 'نشِط'}
              </span>
            </div>

            <div className="subx-foot">
              {sub ? (
                <div className="subx-price">
                  {money(sub.price, sub.currency === 'EGP' ? 'ج.م' : sub.currency)}
                  <small>/ {CYCLE_LABELS[sub.cycle] ?? sub.cycle}</small>
                </div>
              ) : (
                <div className="subx-price">
                  مجّانًا
                  <small>خلال فترة التجربة</small>
                </div>
              )}
              <button
                type="button"
                className="subx-upgrade"
                onClick={() =>
                  document
                    .getElementById('plan-picker')
                    ?.scrollIntoView({ behavior: 'smooth', block: 'start' })
                }
              >
                <Icon name="arrowUp" size={15} /> ترقية الباقة
              </button>
              {cancelAtPeriodEnd ? (
                <button
                  type="button"
                  className="subx-ghost"
                  onClick={() => cancelMut.mutate(false)}
                  disabled={cancelMut.isPending}
                >
                  <Icon name="refresh" size={15} /> استئناف التجديد
                </button>
              ) : (
                <button
                  type="button"
                  className="subx-ghost"
                  onClick={() => setConfirmCancel(true)}
                  disabled={cancelMut.isPending}
                >
                  <Icon name="x" size={15} /> إلغاء التجديد
                </button>
              )}
            </div>
          </div>

          {/* عدّاداتُ الاستخدام: المدّة والمقاعد */}
          <div className="card">
            <div className="card-head">
              <h2 className="card-title">
                <Icon name="chart" size={16} /> الاستخدام
              </h2>
              {tenantStatus && <Badge kind={tenantStatus.kind}>{tenantStatus.label}</Badge>}
            </div>
            <div className="card-body">
              <div className="meter-row" style={{ marginTop: 0 }}>
                <Meter
                  icon="calendar"
                  label={sub ? 'نهاية الفترة الحالية' : 'نهاية التجربة'}
                  value={endsAt ? date(endsAt) : '—'}
                  pct={periodPct}
                  tone={daysLeft !== null && daysLeft <= 7 ? 'danger' : 'brand'}
                  note={
                    daysLeft !== null
                      ? `${daysLeft > 0 ? `متبقٍّ ${plural(daysLeft, WORDS.day)}` : 'انتهت'}${
                          sub?.cancelAtPeriodEnd ? ' — لن يتجدّد' : ''
                        }`
                      : undefined
                  }
                  neg={daysLeft !== null && daysLeft <= 7}
                />
                <Meter
                  icon="users"
                  label="المقاعد المستخدمة"
                  value={
                    <>
                      {data.seats.used}
                      {data.seats.limit !== -1 && <small> / {data.seats.limit}</small>}
                    </>
                  }
                  pct={seatsPct}
                  tone={seatsPct >= 90 ? 'warn' : 'brand'}
                  note={
                    data.seats.limit === -1
                      ? 'مقاعد بلا حدّ'
                      : `${Math.max(data.seats.limit - data.seats.used, 0)} مقعدًا متاحًا`
                  }
                />
              </div>

              {sub?.graceEndsAt && (
                <div className="alert alert-warning mt-4">
                  <Icon name="alert" size={15} />
                  <div>
                    مهلةُ السداد تنتهي في {date(sub.graceEndsAt)} — بعدها يتوقّف الحساب عن قبول
                    التعديل.
                  </div>
                </div>
              )}
            </div>
          </div>

          <PlanManager
            currentCode={sub?.plan.code ?? null}
            cancelAtPeriodEnd={sub?.cancelAtPeriodEnd ?? false}
            endsAt={endsAt}
          />

          <h2 className="acc-sec-title">
            <span className="acc-sec-ic">
              <Icon name="wallet" size={17} />
            </span>
            الفواتير والدفع
          </h2>

          <PaymentMethodCard />

          <LocalTransferCard />

          <InvoiceList invoices={data.invoices} invCtx={invCtx} onPreview={setPreview} />
        </>
      )}

      {preview && (
        <Modal title="معاينة الفاتورة" onClose={() => setPreview(null)} wide>
          <iframe className="inv-preview" srcDoc={preview} title="معاينة الفاتورة" />
          <div className="row mt-3" style={{ gap: 'var(--sp-2)' }}>
            <button className="btn btn-primary" onClick={() => printHtml(preview)}>
              <Icon name="download" size={15} /> تحميل PDF
            </button>
            <button className="btn" onClick={() => setPreview(null)}>
              إغلاق
            </button>
          </div>
        </Modal>
      )}

      {confirmCancel && (
        <Modal title="إلغاء التجديد" onClose={() => setConfirmCancel(false)}>
          <div className="alert alert-warning" style={{ margin: 0 }}>
            <Icon name="alert" size={15} />
            <div>
              سيبقى وصولك حتى نهاية المدّة الحالية{endsAt ? ` (${date(endsAt)})` : ''}، ثمّ يتوقّف
              الاشتراكُ ولا يُدفع تلقائيًّا بعدها. يمكنك استئناف التجديد قبل ذلك في أيّ وقت.
            </div>
          </div>
          <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
            <button
              className="btn btn-danger"
              onClick={() => cancelMut.mutate(true)}
              disabled={cancelMut.isPending}
            >
              {cancelMut.isPending ? 'جارٍ الإلغاء' : 'تأكيد إلغاء التجديد'}
            </button>
            <button
              className="btn"
              onClick={() => setConfirmCancel(false)}
              disabled={cancelMut.isPending}
            >
              تراجع
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}

/**
 * قائمةُ فواتيرِ الاشتراك — تعرضُ نحوَ خمسٍ ثمّ تُمرَّر، مع بحثٍ برقمِ الفاتورةِ
 * أو الشهر (يطابقُ الرقمَ، واسمَ الشهرِ العربيَّ، والتاريخَ الرقميَّ ٢٠٢٦-٠٩).
 */
function InvoiceList({
  invoices,
  invCtx,
  onPreview,
}: {
  invoices: InvoiceRow[];
  invCtx: { planName: string; cycle: string; customer: string };
  onPreview: (html: string) => void;
}) {
  const [q, setQ] = useState('');
  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return invoices;
    return invoices.filter((inv) =>
      [inv.number, date(inv.issuedAt), date(inv.dueAt), inv.issuedAt.slice(0, 7), inv.dueAt.slice(0, 7)]
        .join(' ')
        .toLowerCase()
        .includes(term),
    );
  }, [invoices, q]);

  return (
    <div className="card mt-4">
      <div className="card-head">
        <h2 className="card-title">
          <Icon name="receipt" size={16} /> فواتير الاشتراك
        </h2>
        {invoices.length > 0 && (
          <div className="hd-search">
            <Icon name="search" size={14} />
            <input
              className="hd-search-input"
              placeholder="ابحث برقم الفاتورة أو الشهر…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {q && (
              <button
                type="button"
                className="hd-search-clear"
                onClick={() => setQ('')}
                aria-label="مسح البحث"
              >
                <Icon name="x" size={13} />
              </button>
            )}
          </div>
        )}
      </div>
      <div className="card-body">
        {invoices.length === 0 ? (
          <p className="tiny mute" style={{ margin: 0 }}>
            لا فواتير بعد — تُصدر أوّلُ فاتورةٍ مع أوّل اشتراكٍ مدفوع.
          </p>
        ) : filtered.length === 0 ? (
          <p className="tiny mute" style={{ margin: 0 }}>
            لا فاتورةَ تطابقُ «{q}».
          </p>
        ) : (
          <div className={`inv-list${filtered.length > 5 ? ' inv-scroll' : ''}`}>
            {filtered.map((inv) => {
              const st = INVOICE_STATUS[inv.status] ?? { label: inv.status, kind: 'neutral' };
              return (
                <div key={inv.id} className="inv-row">
                  <span className="inv-ic">
                    <Icon name="receipt" size={17} />
                  </span>
                  <div className="inv-main">
                    <span className="inv-num num" dir="ltr">
                      {inv.number}
                    </span>
                    <div className="tiny mute">
                      صدرت {date(inv.issuedAt)} · تستحقّ {date(inv.dueAt)}
                      {inv.paidAt ? ` · دُفعت ${date(inv.paidAt)}` : ''}
                    </div>
                  </div>
                  <span className="inv-amt">
                    {money(inv.total, inv.currency === 'EGP' ? 'ج.م' : inv.currency)}
                  </span>
                  <Badge kind={st.kind}>{st.label}</Badge>
                  <button
                    className="btn btn-sm btn-icon"
                    title="معاينة"
                    aria-label="معاينة الفاتورة"
                    onClick={() => onPreview(invoiceHtml(inv, invCtx))}
                  >
                    <Icon name="eye" size={15} />
                  </button>
                  <button
                    className="btn btn-sm btn-icon"
                    title="تحميل PDF"
                    aria-label="تحميل الفاتورة PDF"
                    onClick={() => printHtml(invoiceHtml(inv, invCtx))}
                  >
                    <Icon name="download" size={15} />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

/** عدّادُ استخدامٍ واحد: أيقونةٌ وقيمةٌ وشريطُ تقدّمٍ ملوّن. */
function Meter({
  icon,
  label,
  value,
  pct,
  tone = 'brand',
  note,
  neg,
}: {
  icon: 'calendar' | 'users';
  label: string;
  value: ReactNode;
  pct: number;
  tone?: 'brand' | 'warn' | 'danger';
  note?: string;
  neg?: boolean;
}) {
  const fillClass = tone === 'warn' ? ' is-warn' : tone === 'danger' ? ' is-danger' : '';
  return (
    <div className="meter">
      <div className="meter-head">
        <span className="meter-ic">
          <Icon name={icon} size={14} />
        </span>
        {label}
      </div>
      <div className="meter-val">{value}</div>
      <div className="meter-bar">
        <span className={`meter-fill${fillClass}`} style={{ width: `${pct}%` }} />
      </div>
      {note && <div className={`meter-note${neg ? ' neg' : ''}`}>{note}</div>}
    </div>
  );
}

/* ---------------- إدارةُ الباقة: ترقيةٌ وإلغاء ---------------- */

interface PlanRow {
  code: string;
  name: string;
  description: string;
  price: number;
  currency: string;
  cycle: string;
  badge: string;
  features: string[];
}

function PlanManager({
  currentCode,
  cancelAtPeriodEnd,
  endsAt,
}: {
  currentCode: string | null;
  cancelAtPeriodEnd: boolean;
  endsAt: string | null;
}) {
  const toast = useToast();
  const qc = useQueryClient();
  const [confirmPlan, setConfirmPlan] = useState<PlanRow | null>(null);

  const plans = useQuery({
    queryKey: ['settings', 'billing', 'plans'],
    queryFn: () => api<PlanRow[]>('/settings/billing/plans'),
  });

  const change = useMutation({
    mutationFn: (planCode: string) =>
      api('/settings/billing/plan', { method: 'PATCH', body: { planCode } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'billing'] });
      setConfirmPlan(null);
      toast('تغيّرت باقتك', 'success');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر التغيير'),
  });

  /* الترويجُ للأعلى: أغلى باقةٍ فوق الحاليّة هي الموصى بها. */
  const currentPrice = plans.data?.find((p) => p.code === currentCode)?.price ?? 0;
  const upgrades = (plans.data ?? []).filter((p) => p.price > currentPrice);
  const topUpgrade = upgrades.length
    ? upgrades.reduce((a, b) => (b.price > a.price ? b : a)).code
    : null;

  return (
    <div className="card mt-4" id="plan-picker">
      <div className="card-head">
        <h2 className="card-title">
          <Icon name="diamond" size={16} /> الباقات
        </h2>
      </div>
      <div className="card-body">
        {cancelAtPeriodEnd && (
          <div className="alert alert-warning mb-4">
            <Icon name="alert" size={15} />
            <div>
              اشتراكك مُلغى ولن يتجدّد{endsAt ? ` بعد ${date(endsAt)}` : ''}. يبقى وصولك حتى ذلك
              الحين، ويمكنك اختيار باقةٍ لاستئناف الاشتراك.
            </div>
          </div>
        )}

        {plans.isPending && <div className="skeleton" style={{ height: 240 }} />}
        {plans.data && (
          <div className="plan-grid">
            {plans.data.map((p) => {
              const isCurrent = p.code === currentCode && !cancelAtPeriodEnd;
              const isUpgrade = p.price > currentPrice;
              const rec = p.code === topUpgrade && !isCurrent;
              const down = p.price < currentPrice && !isCurrent;
              return (
                <div
                  key={p.code}
                  className={`plan-opt${isCurrent ? ' is-current' : ''}${rec ? ' is-rec' : ''}${
                    down ? ' is-down' : ''
                  }`}
                >
                  {rec && <span className="plan-opt-ribbon">{p.badge || 'الأفضل قيمةً'}</span>}
                  {isCurrent && <span className="plan-opt-tag">باقتك الحالية</span>}
                  <div className="plan-opt-name">{p.name}</div>
                  <div className="plan-opt-price">
                    <span className="num">
                      {money(p.price, p.currency === 'EGP' ? 'ج.م' : p.currency)}
                    </span>
                    <small>/ {CYCLE_LABELS[p.cycle] ?? p.cycle}</small>
                  </div>
                  {p.description && <p className="plan-opt-desc">{p.description}</p>}
                  {p.features.length > 0 && (
                    <ul className="plan-opt-feats">
                      {p.features.slice(0, 5).map((f, i) => (
                        <li key={i}>
                          <Icon name="check" size={13} />
                          {featureLabel(f)}
                        </li>
                      ))}
                    </ul>
                  )}
                  <button
                    className={`btn btn-sm${rec || isUpgrade ? ' btn-primary' : ''}`}
                    disabled={isCurrent || change.isPending}
                    onClick={() => setConfirmPlan(p)}
                  >
                    {isCurrent ? 'باقتك الحالية' : isUpgrade ? 'ترقية' : 'اختيار'}
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {confirmPlan && (
        <Modal title="تأكيد تغيير الباقة" onClose={() => setConfirmPlan(null)}>
          <div className="confirm-plan">
            <div className="confirm-plan-badge">
              <Icon name="diamond" size={26} />
            </div>
            <p className="small" style={{ margin: 0 }}>
              أنت على وشك التحوّل إلى باقة <b>{confirmPlan.name}</b> بسعر{' '}
              <b className="num">
                {money(confirmPlan.price, confirmPlan.currency === 'EGP' ? 'ج.م' : confirmPlan.currency)}
              </b>{' '}
              {CYCLE_LABELS[confirmPlan.cycle] ?? confirmPlan.cycle}.
            </p>
            <div className="alert alert-info" style={{ marginTop: 'var(--sp-3)' }}>
              <Icon name="info" size={15} />
              <div>
                يُدفع الاشتراك من وقت الترقية، وتبدأ مدّةٌ جديدةٌ اليوم — فيصبح تاريخ التجديد بعد
                {confirmPlan.cycle === 'YEARLY' ? ' سنة' : ' شهر'} من الآن.
              </div>
            </div>
          </div>
          <div className="row mt-4" style={{ gap: 'var(--sp-2)' }}>
            <button
              className="btn btn-primary"
              onClick={() => change.mutate(confirmPlan.code)}
              disabled={change.isPending}
            >
              {change.isPending ? 'جارٍ التحويل' : 'تأكيد والترقية'}
            </button>
            <button className="btn" onClick={() => setConfirmPlan(null)} disabled={change.isPending}>
              إلغاء
            </button>
          </div>
        </Modal>
      )}

    </div>
  );
}

/* ---------------- طريقة الدفع ---------------- */

const CARD_BRANDS: Array<{ label: string; test: RegExp }> = [
  { label: 'Visa', test: /^4/ },
  { label: 'Mastercard', test: /^(5[1-5]|2[2-7])/ },
  { label: 'American Express', test: /^3[47]/ },
  { label: 'مدى', test: /^(9682|588845|440647|440795|446404|457865|968208)/ },
];

function brandOf(num: string): string {
  const d = num.replace(/\D/g, '');
  return CARD_BRANDS.find((b) => b.test.test(d))?.label ?? 'بطاقة';
}

/**
 * أدواتُ الدفعِ بالبطاقة في ترويسةِ «طريقة الدفع» — مُصغَّرةٌ بجانب «إضافة بطاقة»:
 * مبدِّلُ النمطِ (تلقائي/يدوي) وزرُّ «ادفع الآن».
 *
 * البطاقةُ مقنَّعةٌ بلا بوّابةِ دفع؛ فالنمطُ (autopay) يُحفظ، و«ادفع الآن» يسجّل
 * طلبَ خصمٍ يعالجه فريقُ الفوترةِ — بلا خصمٍ وهميّ — حتى تُربَط البوّابة.
 */
function CardPayControls({ billing }: { billing: Billing }) {
  const toast = useToast();
  const qc = useQueryClient();
  const info = billing.billingInfo ?? {};
  const autopay = info.autopay ?? false;
  const price = billing.subscription?.price ?? 0;
  const currency = billing.subscription?.currency ?? 'EGP';
  const cur = currency === 'EGP' ? 'ج.م' : currency;
  const pending = (billing.transfers ?? [])[0]?.status === 'pending';

  /* قسطٌ واحدٌ شهريًّا للبطاقةِ أيضًا: الدفعُ اليدويُّ يُتاح عند حلولِ الاستحقاق
   * فقط، والتلقائيُّ يُخصَم كلَّ شهرٍ في موعدِ التجديد. */
  const sub = billing.subscription ?? null;
  const endsAt = sub?.currentPeriodEnd ?? null;
  const daysLeft = endsAt
    ? Math.max(0, Math.ceil((new Date(endsAt).getTime() - Date.now()) / 86_400_000))
    : null;
  const isDue = !sub || daysLeft === null || daysLeft <= 0 || !!sub?.graceEndsAt;
  const canPay = isDue && !autopay && !pending && price > 0;

  const setMode = useMutation({
    mutationFn: (v: boolean) =>
      api('/settings/billing/info', { method: 'PATCH', body: { ...info, autopay: v } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'billing'] });
      toast('حُفظ نمطُ الدفع', 'success');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const payNow = useMutation({
    mutationFn: () =>
      api('/settings/billing/transfer', {
        method: 'POST',
        body: { method: 'card', amount: Math.round(price) },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'billing'] });
      toast('أُرسل طلبُ الدفعِ بالبطاقة — بانتظارِ المعالجة', 'success');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر إرسالُ الطلب'),
  });

  return (
    <>
      <div className="autopay-seg" role="group" aria-label="نمط الدفع بالبطاقة">
        <button
          type="button"
          className={`autopay-opt${autopay ? ' is-on' : ''}`}
          onClick={() => !autopay && setMode.mutate(true)}
          disabled={setMode.isPending}
          title="يُخصَم من البطاقة الافتراضيّة تلقائيًّا عند التجديد"
        >
          <Icon name="refresh" size={13} /> تلقائيّ
        </button>
        <button
          type="button"
          className={`autopay-opt${!autopay ? ' is-on' : ''}`}
          onClick={() => autopay && setMode.mutate(false)}
          disabled={setMode.isPending}
          title="تدفع بنفسك بالزرّ عند كلّ استحقاق"
        >
          <Icon name="user" size={13} /> يدويّ
        </button>
      </div>
      <button
        className="btn btn-sm btn-primary"
        onClick={() => payNow.mutate()}
        disabled={payNow.isPending || !canPay}
        title={
          autopay
            ? 'الخصمُ تلقائيٌّ كلَّ شهرٍ عند الاستحقاق'
            : pending
              ? 'لديك طلبُ دفعٍ قيدَ المراجعة'
              : !isDue
                ? `يُتاح الدفعُ عند الاستحقاق${daysLeft !== null ? ` (باقٍ ${daysLeft})` : ''}`
                : 'يسجّل طلبَ خصمٍ من البطاقةِ يعالجه فريقُ الفوترة'
        }
      >
        <Icon name={!isDue && !autopay ? 'clock' : 'wallet'} size={14} />{' '}
        {payNow.isPending
          ? 'جارٍ الإرسال'
          : autopay
            ? 'خصمٌ تلقائيّ'
            : pending
              ? 'قيدَ المراجعة'
              : !isDue
                ? 'الدفعُ عند الاستحقاق'
                : `ادفع الآن — ${money(price, cur)}`}
      </button>
    </>
  );
}

function PaymentMethodCard() {
  const toast = useToast();
  const qc = useQueryClient();
  const billing = useQuery({
    queryKey: ['settings', 'billing'],
    queryFn: () => api<Billing>('/settings/billing'),
  });
  const cards = billing.data?.cards ?? [];
  const [adding, setAdding] = useState(false);
  const [editCard, setEditCard] = useState<CardRow | null>(null);

  /* تمريرُ شريطِ البطاقاتِ أفقيًّا بعجلةِ الماوس. onWheel في React سلبيٌّ فلا
   * يقبلُ preventDefault، لذا نربطُ مستمعًا أصليًّا غيرَ سلبيّ. ولأنّ الشريطَ
   * ملتقِطٌ (snap mandatory) وحسابُ scrollLeft في RTL غيرُ موثوق، ننتقلُ بطاقةً
   * بطاقةً عبر scrollIntoView (يراعي RTL) مع قفلٍ قصيرٍ يمنع القفزَ عدّةَ بطاقات. */
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let lock = false;
    const onWheel = (e: WheelEvent) => {
      const items = Array.from(el.querySelectorAll<HTMLElement>('.credit-card'));
      if (items.length <= 1) return; // لا فائضَ يُمرَّر
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return; // تمريرٌ أفقيٌّ أصلًا
      e.preventDefault();
      if (lock) return;
      const mid = el.getBoundingClientRect().left + el.clientWidth / 2;
      let curr = 0;
      let best = Infinity;
      items.forEach((c, i) => {
        const r = c.getBoundingClientRect();
        const d = Math.abs(r.left + r.width / 2 - mid);
        if (d < best) {
          best = d;
          curr = i;
        }
      });
      const next = Math.min(items.length - 1, Math.max(0, curr + (e.deltaY > 0 ? 1 : -1)));
      if (next === curr) return;
      lock = true;
      /* انتقالٌ فوريّ (لا smooth): موثوقٌ ويراعي reduced-motion، وحسابُ RTL
       * متروكٌ لـ scrollIntoView. */
      items[next].scrollIntoView({ inline: 'center', block: 'nearest' });
      window.setTimeout(() => {
        lock = false;
      }, 250);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [cards.length, adding]);

  const refresh = () => void qc.invalidateQueries({ queryKey: ['settings', 'billing'] });

  const act = useMutation({
    mutationFn: (v: { action: 'remove' | 'default'; id: string }) =>
      api('/settings/billing/card', { method: 'PATCH', body: v }),
    onSuccess: (_d, v) => {
      refresh();
      toast(v.action === 'remove' ? 'حُذفت البطاقة' : 'صارت الافتراضيّة', 'success');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر التنفيذ'),
  });

  return (
    <div className="card">
      <div className="card-head">
        <h2 className="card-title">
          <Icon name="wallet" size={16} /> طريقة الدفع
        </h2>
        <div className="row card-pay-head" style={{ gap: 'var(--sp-2)', alignItems: 'center' }}>
          {cards.length > 0 && billing.data && <CardPayControls billing={billing.data} />}
          {!adding && (
            <button className="btn btn-sm" onClick={() => setAdding(true)}>
              <Icon name="plus" size={14} /> إضافة بطاقة
            </button>
          )}
        </div>
      </div>
      <div className="card-body">
        <div className="pay-layout">
          <div className="pay-cards-side">
            {adding || cards.length === 0 ? (
              <CardForm
                onDone={() => setAdding(false)}
                onCancel={cards.length > 0 ? () => setAdding(false) : undefined}
              />
            ) : (
              /* شريطُ بطاقاتٍ يُمرَّر بينها في المكان نفسه — كلُّ الأدواتِ على
               * البطاقةِ ذاتها: النجمةُ للافتراضيّة، والقلمُ للتعديل، والسلّةُ
               * للحذف — كلُّها في الزاويةِ العلويّةِ بعيدًا عن اللوجو والبيانات. */
              <div className="pay-cards-scroll" ref={scrollRef}>
                {cards.map((c) => (
                  <div key={c.id} className={`credit-card${c.isDefault ? ' is-default' : ''}`}>
                    <div className="cc-shine" />
                    <div className="cc-actions">
                      <button
                        className={`cc-act${c.isDefault ? ' is-on' : ''}`}
                        title={c.isDefault ? 'البطاقة الافتراضيّة' : 'اجعلها الافتراضيّة'}
                        aria-label={c.isDefault ? 'البطاقة الافتراضيّة' : 'اجعلها الافتراضيّة'}
                        onClick={() => !c.isDefault && act.mutate({ action: 'default', id: c.id })}
                        disabled={c.isDefault || act.isPending}
                      >
                        <Icon name="star" size={15} />
                      </button>
                      <button
                        className="cc-act"
                        title="تعديل البطاقة"
                        aria-label="تعديل البطاقة"
                        onClick={() => setEditCard(c)}
                      >
                        <Icon name="edit" size={14} />
                      </button>
                      <button
                        className="cc-act cc-act-danger"
                        title={cards.length <= 1 ? 'أضف بطاقةً بديلةً أولًا' : 'حذف البطاقة'}
                        aria-label="حذف البطاقة"
                        onClick={() => act.mutate({ action: 'remove', id: c.id })}
                        disabled={cards.length <= 1 || act.isPending}
                      >
                        <Icon name="trash" size={14} />
                      </button>
                    </div>
                    <div className="cc-top">
                      <span className="cc-goy">
                        <span className="cc-goy-logo">
                          <Logo size={22} mono />
                        </span>
                        GoyOne
                      </span>
                    </div>
                    <div className="cc-chiprow">
                      <div className="cc-chip" aria-hidden="true" />
                      <svg className="cc-wave" viewBox="0 0 24 24" aria-hidden="true">
                        <path d="M8 6a10 10 0 0 1 0 12M12 4a14 14 0 0 1 0 16M4 8a6 6 0 0 1 0 8" />
                      </svg>
                    </div>
                    <div className="cc-number num" dir="ltr">
                      •••• •••• •••• {c.last4}
                    </div>
                    <div className="cc-bottom">
                      <div className="cc-field">
                        <span className="cc-label">حامل البطاقة</span>
                        <span className="cc-value">{c.holder || '—'}</span>
                      </div>
                      <div className="cc-field cc-exp">
                        <span className="cc-label">صالحة حتى</span>
                        <span className="cc-value num" dir="ltr">
                          {String(c.expMonth).padStart(2, '0')}/{String(c.expYear).slice(-2)}
                        </span>
                      </div>
                      <span className="cc-brand">{c.brand ?? 'بطاقة'}</span>
                    </div>
                    {c.isDefault && (
                      <span className="cc-default">
                        <Icon name="check" size={11} /> الافتراضيّة
                      </span>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          <BillingInfoBlock info={billing.data?.billingInfo ?? {}} />
        </div>

        <p className="tiny mute mt-3">
          تُحفظ آخر أربعة أرقامٍ فقط للعرض والتجديد — لا يصل الرقمُ الكامل ولا رمز التحقّق (CVV)
          خوادمَنا. ولا تُحذف آخرُ بطاقةٍ إلّا بعد إضافة بديلٍ للتجديد التلقائيّ. والخصمُ التلقائيُّ
          الفوريُّ يتطلّب ربطَ بوّابةِ دفعٍ (Paymob/Fawry/Stripe)؛ حتى ذلك الحين يُسجَّل طلبُ الدفعِ
          ويعالجه فريقُ فوترةِ GoyOne.
        </p>
      </div>

      {editCard && <CardEditModal card={editCard} onDone={() => setEditCard(null)} />}
    </div>
  );
}

/** تعديلُ بطاقة: الاسمُ والصلاحيّةُ فقط — الرقمُ لا يُخزَّن كاملًا فلا يُعدَّل. */
function CardEditModal({ card, onDone }: { card: CardRow; onDone: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [holder, setHolder] = useState(card.holder ?? '');
  const [exp, setExp] = useState(
    card.expMonth && card.expYear
      ? `${String(card.expMonth).padStart(2, '0')}/${String(card.expYear).slice(-2)}`
      : '',
  );

  const expOk = /^(0[1-9]|1[0-2])\s*\/\s*\d{2}$/.test(exp);

  const save = useMutation({
    mutationFn: () => {
      const [mm, yy] = exp.split('/').map((s) => s.trim());
      return api('/settings/billing/card/edit', {
        method: 'PATCH',
        body: {
          id: card.id,
          holder: holder.trim() || undefined,
          expMonth: Number(mm),
          expYear: 2000 + Number(yy),
        },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'billing'] });
      toast('حُفظت بيانات البطاقة', 'success');
      onDone();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <Modal onClose={onDone} title="تعديل البطاقة">
      <p className="tiny mute" style={{ marginTop: 0 }}>
        بطاقة <span className="num" dir="ltr">•••• {card.last4}</span> — يمكن تعديلُ الاسمِ
        والصلاحيّة. لتغيير الرقم أضف بطاقةً جديدةً واحذف هذه.
      </p>
      <label className="field">
        <span className="label">اسم حامل البطاقة</span>
        <input
          className="input"
          value={holder}
          maxLength={120}
          onChange={(e) => setHolder(e.target.value)}
        />
      </label>
      <label className="field mt-3">
        <span className="label req">الصلاحية</span>
        <input
          className="input num"
          dir="ltr"
          inputMode="numeric"
          placeholder="MM/YY"
          value={exp}
          maxLength={5}
          onChange={(e) => {
            const v = e.target.value.replace(/[^\d]/g, '').slice(0, 4);
            setExp(v.length > 2 ? `${v.slice(0, 2)}/${v.slice(2)}` : v);
          }}
        />
      </label>
      <div className="row mt-3" style={{ gap: 'var(--sp-2)' }}>
        <button
          className="btn btn-primary btn-sm"
          onClick={() => save.mutate()}
          disabled={!expOk || save.isPending}
        >
          {save.isPending ? 'جارٍ الحفظ' : 'حفظ'}
        </button>
        <button className="btn btn-sm" onClick={onDone} disabled={save.isPending}>
          إلغاء
        </button>
      </div>
    </Modal>
  );
}

/* ---------------- الدفعُ بتحويلٍ محليٍّ: إنستاباي وفودافون كاش ---------------- */

const INSTAPAY_ADDRESS = 'sunmed.agency@instapay';
const INSTAPAY_LINK = 'https://ipn.eg/S/sunmed.agency/instapay/7I4o8S';
const VODAFONE_NUMBER = '01146293399';
const VODAFONE_LINK = 'http://vf.eg/vfcash?id=mt&qrId=eTom4z';

const TRANSFER_METHODS: Record<string, string> = {
  instapay: 'إنستاباي',
  vodafone_cash: 'فودافون كاش',
};

const TRANSFER_STATUS: Record<string, { label: string; kind: 'success' | 'warning' | 'danger' }> = {
  pending: { label: 'قيد المراجعة', kind: 'warning' },
  confirmed: { label: 'تم التأكيد', kind: 'success' },
  rejected: { label: 'لم يُقبل', kind: 'danger' },
};

/* علامتا الوسيلتين مرسومتان داخليًّا (لا رابطَ خارجيّ): إنستاباي بتدرّجٍ
 * بنفسجيٍّ وسهمَي تحويل، وفودافون بعلامةِ الاقتباسِ الحمراءِ المميّزة. */
function InstaPayMark() {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%" aria-hidden="true">
      <defs>
        <linearGradient id="ipgrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#7c3aed" />
          <stop offset="1" stopColor="#c026d3" />
        </linearGradient>
      </defs>
      <rect width="40" height="40" rx="10" fill="url(#ipgrad)" />
      <path
        d="M12 16h13l-3.4-3.4M28 24H15l3.4 3.4"
        fill="none"
        stroke="#fff"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
function VodafoneMark() {
  return (
    <svg viewBox="0 0 40 40" width="100%" height="100%" aria-hidden="true">
      <circle cx="20" cy="20" r="20" fill="#e60000" />
      <path
        d="M24.6 7.7c-6.4.2-11.3 5.4-11.3 11.5 0 5.4 3.9 9.1 8.7 9.1 4.7 0 8.1-3.2 8.1-7.6 0-2.9-1.9-5.4-4.9-5.4-1 0-2 .3-2.6.7 1.2-3.7 4.4-6.5 8.2-7.6-1.9-1.7-4.6-2.2-6.2-2.2Z"
        fill="#fff"
      />
    </svg>
  );
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * وسيلةُ تحويلٍ واحدة: ضغطةٌ تفتح رابطَ التحويلِ مباشرةً، وزرٌّ يُظهر رمزَ QR
 * (مُولَّدٌ من الرابطِ ذاتِه فيُطابقه، وعلامةُ الوسيلةِ في وسطه) للمسحِ بالهاتف.
 */
function TransferMethodTile({
  kind,
  label,
  mark,
  addr,
  copyLabel,
  link,
  poweredBy,
}: {
  kind: 'instapay' | 'vodafone';
  label: string;
  mark: ReactNode;
  addr: string;
  copyLabel: string;
  link: string;
  poweredBy?: string;
}) {
  const toast = useToast();
  const [qr, setQr] = useState('');
  const [showQr, setShowQr] = useState(false);

  const toggleQr = async () => {
    if (!showQr && !qr) {
      try {
        setQr(await QRCode.toDataURL(link, { margin: 1, width: 320, errorCorrectionLevel: 'H' }));
      } catch {
        toast('تعذّر توليدُ رمز QR.');
        return;
      }
    }
    setShowQr((v) => !v);
  };

  return (
    <div className="transfer-method">
      <div className="transfer-method-head">
        <span className="transfer-logo">{mark}</span>
        <span>{label}</span>
      </div>
      <div className="transfer-addr">
        <span className="num" dir="ltr">
          {addr}
        </span>
        <button
          className="btn btn-sm btn-icon"
          title={`نسخ ${copyLabel}`}
          aria-label={`نسخ ${copyLabel}`}
          onClick={() => void copyText(addr).then((ok) => ok && toast('تم النسخ', 'success'))}
        >
          <Icon name="clipboard" size={14} />
        </button>
      </div>
      <div className="transfer-actions">
        <a
          className="btn btn-sm btn-primary transfer-open"
          href={link}
          target="_blank"
          rel="noreferrer"
        >
          <Icon name="arrowRightLeft" size={14} /> افتح وحوِّل
        </a>
        <button className="btn btn-sm transfer-qr-btn" onClick={() => void toggleQr()}>
          <Icon name="qr" size={14} /> {showQr ? 'إخفاء الرمز' : 'إظهار رمز QR'}
        </button>
      </div>
      {showQr && qr && (
        <div className={`transfer-qr tq-${kind}`}>
          <div className="transfer-qr-img">
            <img src={qr} alt={`رمز ${label}`} />
            <span className="transfer-qr-mark">{mark}</span>
          </div>
          <span className="tiny num" dir="ltr">
            {addr}
          </span>
          {poweredBy && <span className="tiny mute">{poweredBy}</span>}
        </div>
      )}
    </div>
  );
}

/**
 * وسيلةٌ بديلةٌ للفيزا: يحوّل العميلُ عبر إنستاباي أو فودافون كاش ثمّ يرفق
 * الإيصال. تصل الحوالةُ إلى حسابات GoyOne (المنصّة) لا حسابات الشركة نفسها؛
 * فإن أكّدها فريقُ الحسابات صدرت الفاتورة، وإن رفضها ظهر تنبيهٌ بعدم القبول.
 */
function LocalTransferCard() {
  const toast = useToast();
  const qc = useQueryClient();
  const billing = useQuery({
    queryKey: ['settings', 'billing'],
    queryFn: () => api<Billing>('/settings/billing'),
  });
  const price = billing.data?.subscription?.price ?? 0;
  const currency = billing.data?.subscription?.currency ?? 'EGP';
  const cur = currency === 'EGP' ? 'ج.م' : currency;
  const transfers = billing.data?.transfers ?? [];
  const latest = transfers[0];
  const pending = latest?.status === 'pending';

  const [method, setMethod] = useState<'instapay' | 'vodafone_cash'>('instapay');
  const [amount, setAmount] = useState('');
  const [reference, setReference] = useState('');
  const [receipt, setReceipt] = useState('');
  const [reading, setReading] = useState(false);
  const [dragging, setDragging] = useState(false);

  /* يُملأ المبلغُ بسعرِ الباقةِ افتراضيًّا أوّلَ ما تُحمَّل البيانات. */
  useEffect(() => {
    if (price > 0 && !amount) setAmount(String(price));
  }, [price]); // eslint-disable-line react-hooks/exhaustive-deps

  const amountNum = Math.round(Number(amount) || 0);

  const onFile = async (file: File | undefined) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast('أرفق صورةً للإيصال (لقطة شاشة أو صورة).');
      return;
    }
    setReading(true);
    try {
      setReceipt(await downscaleFile(file, 1400, 0.8));
    } catch {
      toast('تعذّرت قراءةُ الصورة.');
    } finally {
      setReading(false);
    }
  };

  const submit = useMutation({
    mutationFn: () =>
      api('/settings/billing/transfer', {
        method: 'POST',
        body: {
          method,
          amount: amountNum,
          reference: reference.trim() || undefined,
          receipt,
        },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'billing'] });
      toast('أُرسل الإيصالُ — بانتظارِ تأكيدِ حسابات GoyOne', 'success');
      setReceipt('');
      setReference('');
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الإرسال'),
  });

  const canSubmit = !!receipt && amountNum > 0 && !submit.isPending && !pending;

  /* بوّابةُ الاستحقاق: قسطٌ واحدٌ شهريًّا. التحويلُ مقفولٌ حتى يحلَّ موعدُ
   * السداد (currentPeriodEnd) — أو يتأخّر، أو تبدأ مهلةُ السماح — أو في
   * التجربةِ بلا اشتراكٍ فيُفتح ليدفعَ أوّلَ قسط. */
  const sub = billing.data?.subscription ?? null;
  const endsAt = sub?.currentPeriodEnd ?? null;
  const startsAt = sub?.currentPeriodStart ?? null;
  const graceEndsAt = sub?.graceEndsAt ?? null;
  const daysLeft = endsAt
    ? Math.max(0, Math.ceil((new Date(endsAt).getTime() - Date.now()) / 86_400_000))
    : null;
  const isDue = !sub || daysLeft === null || daysLeft <= 0 || !!graceEndsAt;
  const periodPct =
    startsAt && endsAt
      ? clampPct(
          ((Date.now() - new Date(startsAt).getTime()) /
            (new Date(endsAt).getTime() - new Date(startsAt).getTime())) *
            100,
        )
      : 0;

  /* مطويٌّ افتراضيًّا؛ يُفتح تلقائيًّا مرّةً إن كان الدفعُ مستحقًّا أو هناك حوالةٌ
   * قيدَ المراجعة (يحتاج انتباهَ العميل). */
  const [open, setOpen] = useState(false);
  const autoRef = useRef(false);
  useEffect(() => {
    if (!billing.data || autoRef.current) return;
    autoRef.current = true;
    if (isDue || pending) setOpen(true);
  }, [billing.data, isDue, pending]);

  const headStatus = pending
    ? { label: 'قيدَ المراجعة', cls: 'is-wait' }
    : isDue
      ? { label: 'الدفعُ متاحٌ الآن', cls: 'is-due' }
      : { label: daysLeft !== null ? `باقٍ ${plural(daysLeft, WORDS.day)}` : 'مقفول', cls: 'is-lock' };

  return (
    <div className={`card mt-4 trf-card${open ? ' is-open' : ''}`}>
      <button type="button" className="trf-head" onClick={() => setOpen((v) => !v)}>
        <h2 className="card-title">
          <Icon name="arrowRightLeft" size={16} /> الدفعُ بتحويلٍ محلّيّ
        </h2>
        <span className="trf-head-right">
          <span className={`trf-pill ${headStatus.cls}`}>{headStatus.label}</span>
          <Icon name="chevronDown" size={16} />
        </span>
      </button>
      {open && (
      <div className="card-body">
        {/* حالةُ آخرِ حوالة. */}
        {latest && (
          <div className={`transfer-status is-${latest.status}`}>
            <Icon
              name={
                latest.status === 'confirmed'
                  ? 'check'
                  : latest.status === 'rejected'
                    ? 'alert'
                    : 'clock'
              }
              size={17}
            />
            <div className="transfer-status-body">
              <strong>
                {latest.status === 'confirmed'
                  ? 'تمّ تأكيدُ تحويلِك وصدرت فاتورتُك.'
                  : latest.status === 'rejected'
                    ? 'لم يُقبل التحويلُ — لم يصل إلى حساباتنا.'
                    : 'حوالتُك قيدَ المراجعةِ لدى حساباتِ GoyOne.'}
              </strong>
              <span className="tiny mute">
                {TRANSFER_METHODS[latest.method] ?? latest.method} ·{' '}
                {money(latest.amount, latest.currency === 'EGP' ? 'ج.م' : latest.currency)} ·{' '}
                {date(latest.createdAt)}
              </span>
              {latest.status === 'rejected' && latest.reviewNote && (
                <span className="tiny" style={{ color: 'var(--danger)' }}>
                  السبب: {latest.reviewNote}
                </span>
              )}
            </div>
          </div>
        )}

        {/* مقفول: لم يحلَّ موعدُ السداد بعد — عدّادٌ جرافيكيٌّ للأيّام. */}
        {!isDue && !pending ? (
          <div className="due-lock">
            <div
              className={`due-ring${daysLeft !== null && daysLeft <= 7 ? ' is-soon' : ''}`}
              style={{ ['--pct' as string]: `${periodPct}` }}
            >
              <svg viewBox="0 0 120 120" aria-hidden="true">
                <circle className="due-ring-bg" cx="60" cy="60" r="52" />
                <circle
                  className="due-ring-fg"
                  cx="60"
                  cy="60"
                  r="52"
                  style={{
                    strokeDasharray: 2 * Math.PI * 52,
                    strokeDashoffset: 2 * Math.PI * 52 * (1 - periodPct / 100),
                  }}
                />
              </svg>
              <div className="due-ring-txt">
                <strong>{daysLeft}</strong>
                <span>{daysLeft === 1 ? 'يوم' : 'يومًا'}</span>
              </div>
            </div>
            <div className="due-info">
              <span className="due-badge">
                <Icon name="clock" size={13} /> قسطٌ واحدٌ شهريًّا
              </span>
              <h3>باقٍ على موعدِ سدادِ القسط</h3>
              <p>
                يستحقُّ قسطُك القادمُ في <b>{endsAt ? date(endsAt) : '—'}</b>، وقيمتُه{' '}
                <b>{money(price, cur)}</b>.
              </p>
              <p className="tiny mute" style={{ margin: 0 }}>
                خيارُ التحويلِ مقفولٌ حتى ذلك الحين، ويُفتح تلقائيًّا عند حلولِ الموعد. لا يلزمك
                تحويلٌ أكثرَ من مرّةٍ في الشهر.
              </p>
            </div>
          </div>
        ) : (
        <>
        <p className="tiny mute" style={{ marginTop: 0 }}>
          إن لم تدفع بالفيزا، حوّل قيمةَ الاشتراكِ عبرَ إنستاباي أو فودافون كاش، ثمّ أرفق
          الإيصال. تصلُ الحوالةُ إلى حساباتِ GoyOne، وبمجرّدِ تأكيدها تصدرُ فاتورتُك.
        </p>

        {/* وسيلتا التحويل — ضغطةٌ تفتح رابطَ التحويلِ مباشرةً، أو إظهارُ رمزِ QR. */}
        <div className="transfer-methods">
          <TransferMethodTile
            kind="instapay"
            label="إنستاباي"
            mark={<InstaPayMark />}
            addr={INSTAPAY_ADDRESS}
            copyLabel="العنوان"
            link={INSTAPAY_LINK}
            poweredBy="Powered by InstaPay"
          />
          <TransferMethodTile
            kind="vodafone"
            label="فودافون كاش"
            mark={<VodafoneMark />}
            addr={VODAFONE_NUMBER}
            copyLabel="الرقم"
            link={VODAFONE_LINK}
          />
        </div>

        {/* إرفاقُ الإيصالِ بعد التحويل. */}
        <div className="transfer-receipt">
          <h3 className="transfer-receipt-title">
            <Icon name="paperclip" size={15} /> أرفق إيصالَ التحويل
          </h3>
          {pending ? (
            <p className="tiny mute" style={{ margin: 0 }}>
              لديك حوالةٌ قيدَ المراجعةِ الآن — انتظر ردَّ حساباتِ GoyOne قبلَ إرسالِ أخرى.
            </p>
          ) : (
            <div className="trf">
              <div className="trf-grid">
                {/* الحقول — وسيلةٌ ومبلغٌ ومرجعٌ اختياريّ */}
                <div className="trf-fields">
                  <label className="trf-field">
                    <span className="trf-label">
                      <Icon name="arrowRightLeft" size={13} /> وسيلة التحويل
                    </span>
                    <select
                      className="input"
                      value={method}
                      onChange={(e) => setMethod(e.target.value as 'instapay' | 'vodafone_cash')}
                    >
                      <option value="instapay">إنستاباي</option>
                      <option value="vodafone_cash">فودافون كاش</option>
                    </select>
                  </label>
                  <label className="trf-field">
                    <span className="trf-label">
                      <Icon name="wallet" size={13} /> المبلغ المحوَّل
                    </span>
                    <div className="trf-amount">
                      <input
                        className="input num"
                        dir="ltr"
                        inputMode="numeric"
                        value={amount ? Number(amount).toLocaleString('en-US') : ''}
                        maxLength={12}
                        onChange={(e) => setAmount(e.target.value.replace(/\D/g, '').slice(0, 9))}
                      />
                      <span className="trf-amount-cur">{cur}</span>
                    </div>
                  </label>
                  <label className="trf-field">
                    <span className="trf-label">
                      <Icon name="note" size={13} /> مرجع الحوالة <em>اختياريّ</em>
                    </span>
                    <input
                      className="input num"
                      dir="ltr"
                      value={reference}
                      maxLength={60}
                      placeholder="رقم العمليّة"
                      onChange={(e) => setReference(e.target.value)}
                    />
                  </label>
                </div>

                {/* رفعُ الإيصال — منطقةٌ جرافيكيّةٌ بالسحبِ أو النقر */}
                <label
                  className={`trf-upload${receipt ? ' has-file' : ''}${
                    dragging ? ' is-drag' : ''
                  }${reading ? ' is-reading' : ''}`}
                  onDragOver={(e) => {
                    e.preventDefault();
                    if (!receipt) setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    void onFile(e.dataTransfer.files?.[0]);
                  }}
                >
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(e) => void onFile(e.target.files?.[0])}
                  />
                  {receipt ? (
                    <div className="trf-file">
                      <img className="trf-file-img" src={receipt} alt="إيصال التحويل" />
                      <span className="trf-file-badge">
                        <Icon name="check" size={13} /> تمّ إرفاق الإيصال
                      </span>
                      <span className="trf-file-change">
                        <Icon name="edit" size={13} /> تغيير الصورة
                      </span>
                    </div>
                  ) : (
                    <div className="trf-drop">
                      <span className="trf-drop-ic">
                        <Icon name={reading ? 'clock' : 'camera'} size={24} />
                      </span>
                      <strong>
                        {reading
                          ? 'جارٍ قراءةُ الصورة…'
                          : 'اسحب صورةَ الإيصال هنا أو انقر للاختيار'}
                      </strong>
                      <span className="tiny mute">لقطةُ شاشةٍ أو صورةٌ للحوالة — PNG أو JPG</span>
                    </div>
                  )}
                </label>
              </div>

              <div className="trf-actions">
                <button
                  className="btn btn-primary trf-submit"
                  onClick={() => submit.mutate()}
                  disabled={!canSubmit}
                >
                  <Icon name="check" size={15} />{' '}
                  {submit.isPending
                    ? 'جارٍ الإرسال'
                    : `أرسلتُ الحوالة — أكّد الإيصال${amountNum > 0 ? ` (${money(amountNum, cur)})` : ''}`}
                </button>
                {receipt && (
                  <button
                    className="btn"
                    onClick={() => setReceipt('')}
                    disabled={submit.isPending}
                  >
                    <Icon name="trash" size={14} /> إزالة الصورة
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
        </>
        )}
      </div>
      )}
    </div>
  );
}

/* حقولُ الفوترةِ النصّيّة (خارج القوائم المتتالية للدولة/المحافظة/المدينة). */
const BILLING_TEXT_FIELDS: Array<{ key: keyof BillingInfo; label: string; num?: boolean }> = [
  { key: 'firstName', label: 'الاسم الأول' },
  { key: 'lastName', label: 'اسم العائلة' },
  { key: 'company', label: 'الجهة / الشركة' },
  { key: 'taxNumber', label: 'الرقم الضريبي', num: true },
];
/* تفاصيلُ العنوان بعد اختيار المدينة. */
const ADDRESS_FIELDS: Array<{ key: keyof BillingInfo; label: string; num?: boolean }> = [
  { key: 'street', label: 'الشارع' },
  { key: 'buildingNo', label: 'رقم المبنى', num: true },
  { key: 'floor', label: 'الدور', num: true },
  { key: 'apartment', label: 'رقم الشقة', num: true },
  { key: 'postalCode', label: 'الرمز البريدي', num: true },
];

/** أوّلُ حرفين من الاسم لصورةٍ رمزيّةٍ دائريّة. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const chars = parts.slice(0, 2).map((p) => p[0] ?? '');
  return chars.join('') || '؟';
}

function BillingInfoBlock({ info }: { info: BillingInfo }) {
  const [editing, setEditing] = useState(false);
  const has = Object.values(info).some((v) => typeof v === 'string' && v.trim());
  const fullName = [info.firstName, info.lastName].filter(Boolean).join(' ');
  const addr = [
    info.street,
    info.buildingNo && `مبنى ${info.buildingNo}`,
    info.floor && `الدور ${info.floor}`,
    info.apartment && `شقة ${info.apartment}`,
    info.city,
    info.governorate,
    info.country,
    info.postalCode,
  ]
    .filter(Boolean)
    .join('، ');

  if (editing) {
    return <BillingInfoForm info={info} onDone={() => setEditing(false)} />;
  }

  return (
    <div className="billing-info">
      <div className="billing-info-head">
        <span className="billing-info-title">
          <Icon name="receipt" size={15} /> بيانات الفوترة
        </span>
        <button
          className="btn btn-sm btn-icon"
          title="تعديل بيانات الفوترة"
          aria-label="تعديل بيانات الفوترة"
          onClick={() => setEditing(true)}
        >
          <Icon name="edit" size={14} />
        </button>
      </div>
      {has ? (
        <div className="billing-info-cards">
          {fullName && (
            <div className="bi-item">
              <span className="bi-ava">{initialsOf(fullName)}</span>
              <span className="bi-text">
                <span className="bi-k">الاسم</span>
                <span className="bi-v">{fullName}</span>
              </span>
            </div>
          )}
          {info.company && (
            <div className="bi-item">
              <span className="bi-ic bi-ic-green">
                <Icon name="briefcase" size={17} />
              </span>
              <span className="bi-text">
                <span className="bi-k">الجهة</span>
                <span className="bi-v">{info.company}</span>
              </span>
            </div>
          )}
          {info.taxNumber && (
            <div className="bi-item">
              <span className="bi-ic bi-ic-gold">
                <Icon name="receipt" size={17} />
              </span>
              <span className="bi-text">
                <span className="bi-k">الرقم الضريبي</span>
                <span className="bi-v num" dir="ltr">
                  {info.taxNumber}
                </span>
              </span>
            </div>
          )}
          {addr && (
            <div className="bi-item bi-item-tall">
              <span className="bi-ic bi-ic-blue">
                <Icon name="mapPin" size={17} />
              </span>
              <span className="bi-text">
                <span className="bi-k">العنوان</span>
                <span className="bi-v">{addr}</span>
              </span>
            </div>
          )}
        </div>
      ) : (
        <div className="billing-info-empty">
          <p className="tiny mute" style={{ margin: 0 }}>
            لم تُضف بعد بيانات الفوترة (الاسم والعنوان والرقم الضريبيّ) التي تظهر على فاتورتك.
          </p>
          <button className="btn btn-sm btn-primary mt-3" onClick={() => setEditing(true)}>
            <Icon name="plus" size={14} /> أضف بيانات الفوترة
          </button>
        </div>
      )}
    </div>
  );
}

function BillingInfoForm({ info, onDone }: { info: BillingInfo; onDone: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState<BillingInfo>(info);
  const set = (k: keyof BillingInfo, v: string) => setForm((f) => ({ ...f, [k]: v }));

  /* القوائمُ المتتالية: تغييرُ الدولةِ يمسحُ المحافظةَ والمدينة، وتغييرُ
   * المحافظةِ يمسحُ المدينة — كي لا تبقى قيمةٌ لا تنتمي لما فوقها. */
  const setCountry = (v: string) => setForm((f) => ({ ...f, country: v, governorate: '', city: '' }));
  const setGov = (v: string) => setForm((f) => ({ ...f, governorate: v, city: '' }));

  const govs = govsOf(form.country);
  const cities = citiesOf(form.country, form.governorate);
  const govLabel = govLabelOf(form.country);

  const save = useMutation({
    mutationFn: () => api('/settings/billing/info', { method: 'PATCH', body: form }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'billing'] });
      toast('حُفظت بيانات الفوترة', 'success');
      onDone();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  return (
    <div className="billing-info">
      <div className="billing-info-head">
        <span className="billing-info-title">
          <Icon name="receipt" size={15} /> بيانات الفوترة
        </span>
      </div>
      <div className="bi-grid">
        {BILLING_TEXT_FIELDS.map((f) => (
          <label key={f.key} className="field">
            <span className="label">{f.label}</span>
            <input
              className={`input${f.num ? ' num' : ''}`}
              dir={f.num ? 'ltr' : undefined}
              value={(form[f.key] as string | undefined) ?? ''}
              maxLength={200}
              onChange={(e) => set(f.key, e.target.value)}
            />
          </label>
        ))}

        <label className="field">
          <span className="label">الدولة</span>
          <select className="input" value={form.country ?? ''} onChange={(e) => setCountry(e.target.value)}>
            <option value="">— اختر الدولة —</option>
            {COUNTRY_NAMES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="label">{govLabel}</span>
          <select
            className="input"
            value={form.governorate ?? ''}
            onChange={(e) => setGov(e.target.value)}
            disabled={!govs.length}
          >
            <option value="">{form.country ? `— اختر ${govLabel} —` : 'اختر الدولة أولًا'}</option>
            {govs.map((g) => (
              <option key={g.name} value={g.name}>
                {g.name}
              </option>
            ))}
          </select>
        </label>

        <label className="field">
          <span className="label">المدينة</span>
          <select
            className="input"
            value={form.city ?? ''}
            onChange={(e) => set('city', e.target.value)}
            disabled={!cities.length}
          >
            <option value="">{form.governorate ? '— اختر المدينة —' : `اختر ${govLabel} أولًا`}</option>
            {cities.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </label>

        {ADDRESS_FIELDS.map((f) => (
          <label key={f.key} className="field">
            <span className="label">{f.label}</span>
            <input
              className={`input${f.num ? ' num' : ''}`}
              dir={f.num ? 'ltr' : undefined}
              value={(form[f.key] as string | undefined) ?? ''}
              maxLength={200}
              onChange={(e) => set(f.key, e.target.value)}
            />
          </label>
        ))}
      </div>
      <div className="row mt-3" style={{ gap: 'var(--sp-2)' }}>
        <button className="btn btn-primary btn-sm" onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'جارٍ الحفظ' : 'حفظ البيانات'}
        </button>
        <button className="btn btn-sm" onClick={onDone} disabled={save.isPending}>
          إلغاء
        </button>
      </div>
    </div>
  );
}

function CardForm({ onDone, onCancel }: { onDone: () => void; onCancel?: () => void }) {
  const toast = useToast();
  const qc = useQueryClient();
  const [number, setNumber] = useState('');
  const [exp, setExp] = useState('');
  const [cvv, setCvv] = useState('');
  const [holder, setHolder] = useState('');

  const save = useMutation({
    mutationFn: () => {
      const digits = number.replace(/\D/g, '');
      const [mm, yy] = exp.split('/').map((s) => s.trim());
      const expYear = 2000 + Number(yy);
      /* الرقمُ الكاملُ ورمزُ التحقّق لا يغادران المتصفّح — يُرسل المقنَّعُ فقط. */
      return api('/settings/billing/card', {
        method: 'POST',
        body: {
          brand: brandOf(digits),
          last4: digits.slice(-4),
          expMonth: Number(mm),
          expYear,
          holder: holder.trim() || undefined,
        },
      });
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['settings', 'billing'] });
      toast('أُضيفت البطاقة', 'success');
      onDone();
    },
    onError: (e) => toast(e instanceof Error ? e.message : 'تعذّر الحفظ'),
  });

  const digits = number.replace(/\D/g, '');
  const expOk = /^(0[1-9]|1[0-2])\s*\/\s*\d{2}$/.test(exp);
  const valid = digits.length >= 13 && digits.length <= 19 && expOk && cvv.length >= 3;

  return (
    <div className="form-grid">
      <div className="field span-2">
        <label className="label req" htmlFor="cc-holder">
          اسم حامل البطاقة
        </label>
        <input
          id="cc-holder"
          className="input"
          placeholder="كما هو مكتوبٌ على البطاقة"
          value={holder}
          maxLength={120}
          onChange={(e) => setHolder(e.target.value)}
        />
      </div>

      <div className="field span-2">
        <div className="cc-form-row">
          <label className="field cc-f-num">
            <span className="label req">رقم البطاقة</span>
            <input
              className="input num"
              dir="ltr"
              inputMode="numeric"
              autoComplete="off"
              placeholder="0000 0000 0000 0000"
              value={number}
              maxLength={23}
              onChange={(e) =>
                setNumber(
                  e.target.value
                    .replace(/[^\d]/g, '')
                    .replace(/(.{4})/g, '$1 ')
                    .trim(),
                )
              }
            />
            {digits.length >= 2 && <span className="tiny mute">{brandOf(digits)}</span>}
          </label>
          <label className="field cc-f-exp">
            <span className="label req">الصلاحية</span>
            <input
              className="input num"
              dir="ltr"
              inputMode="numeric"
              placeholder="MM/YY"
              value={exp}
              maxLength={5}
              onChange={(e) => {
                const v = e.target.value.replace(/[^\d]/g, '').slice(0, 4);
                setExp(v.length > 2 ? `${v.slice(0, 2)}/${v.slice(2)}` : v);
              }}
            />
          </label>
          <label className="field cc-f-cvv">
            <span className="label req">CVV</span>
            <input
              className="input num"
              dir="ltr"
              inputMode="numeric"
              autoComplete="off"
              placeholder="•••"
              value={cvv}
              maxLength={4}
              onChange={(e) => setCvv(e.target.value.replace(/\D/g, ''))}
            />
          </label>
        </div>
      </div>

      <div className="field span-2">
        <div className="row" style={{ gap: 'var(--sp-2)' }}>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={!valid || save.isPending}
          >
            {save.isPending ? 'جارٍ الإضافة' : 'إضافة البطاقة'}
          </button>
          {onCancel && (
            <button className="btn" onClick={onCancel} disabled={save.isPending}>
              إلغاء
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------------- فاتورةٌ بهوية GoyOne بجودة فواتير العملاء ---------------- */

/* علامةُ GoyOne مضمَّنةً لتظهر في المعاينة وملفّ الـPDF (لا رابطَ خارجيّ). */
const GOY_LOGO = `<svg viewBox="304 315 547 606" width="40" height="44" style="display:block">
  <path d="M607 331 A287 287 0 0 0 607 905 L607 816 A198 198 0 0 1 607 420 Z" fill="#3672ea"/>
  <circle cx="733.5" cy="494.5" r="57.5" fill="#51a521"/>
  <path d="M604 573 H835 V816 H745 V663 H604 Z" fill="#3672ea"/>
</svg>`;

function invoiceHtml(inv: InvoiceRow, ctx: { planName: string; cycle: string; customer: string }): string {
  const st = INVOICE_STATUS[inv.status] ?? { label: inv.status, kind: 'neutral' };
  const cur = inv.currency === 'EGP' ? 'ج.م' : inv.currency;
  const amount = `${inv.total.toLocaleString('en-US', { maximumFractionDigits: 2 })} ${cur}`;
  /* أرقامٌ لاتينيّةٌ في كل الفاتورة — أسماءُ الشهور عربيّةٌ والأرقامُ إنجليزيّة. */
  const fmt = (d: string) =>
    new Date(d).toLocaleDateString('ar-EG-u-nu-latn', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  const desc = `اشتراك GoyOne — ${ctx.planName}${ctx.cycle ? ` (${ctx.cycle})` : ''}`;

  return `<!doctype html><html dir="rtl" lang="ar"><head><meta charset="utf-8">
<meta name="viewport" content="width=device-width, initial-scale=1">
<title>فاتورة ${inv.number} — GoyOne</title>
<style>
  @page { size: A4; margin: 0; }
  * { box-sizing: border-box; }
  html,body { margin:0; }
  body { font-family:'Cairo','Segoe UI','Tahoma',system-ui,sans-serif; color:#16202f; font-size:14px; }
  .sheet { padding:20mm 18mm; }
  .band { display:flex; justify-content:space-between; align-items:flex-start;
    background:linear-gradient(120deg,#1d46a6,#3672ea 60%,#6d5bd0); color:#fff;
    padding:22px 24px; border-radius:14px; }
  .brand { display:flex; align-items:center; gap:12px; }
  .brand .logo { background:#fff; padding:6px; border-radius:10px; line-height:0; }
  .brand .name { font-size:24px; font-weight:800; letter-spacing:.02em; }
  .brand .name small { display:block; font-size:11px; font-weight:500; opacity:.85; letter-spacing:0; }
  .band .doc { text-align:left; }
  .band .doc h1 { margin:0; font-size:20px; }
  .band .doc .num { font-family:'Poppins','Cairo',sans-serif; direction:ltr; opacity:.9; margin-top:2px; }
  .status { display:inline-block; margin-top:8px; padding:4px 12px; border-radius:999px;
    font-size:12px; font-weight:700; background:rgba(255,255,255,.22); color:#fff; }
  .parties { display:flex; gap:32px; margin:26px 2px 0; }
  .parties .col { flex:1; }
  .parties .lbl { font-size:11px; color:#8791a1; text-transform:uppercase; letter-spacing:.06em; }
  .parties .val { font-weight:700; margin-top:2px; }
  .parties .sub { font-size:12px; color:#6f7784; }
  table { width:100%; border-collapse:collapse; margin-top:24px; }
  thead th { background:#f4f6f9; font-size:12px; color:#445064; text-align:right; padding:12px 12px; }
  thead th.amt { text-align:left; }
  tbody td { padding:14px 12px; border-bottom:1px solid #e3e7ed; }
  td.amt { text-align:left; font-family:'Poppins','Cairo',sans-serif; direction:ltr; font-weight:600; }
  .totals { display:flex; justify-content:flex-start; margin-top:18px; }
  .totals .box { min-width:260px; }
  .totals .r { display:flex; justify-content:space-between; padding:7px 2px; font-size:13px; color:#445064; }
  .totals .r .n { font-family:'Poppins','Cairo',sans-serif; direction:ltr; }
  .totals .grand { border-top:2px solid #16202f; margin-top:6px; padding-top:12px;
    font-size:18px; font-weight:800; color:#16202f; }
  .paid-stamp { display:inline-block; margin-top:14px; padding:6px 14px; border:2px solid #51a521;
    color:#51a521; border-radius:8px; font-weight:800; transform:rotate(-4deg); }
  .foot { margin-top:40px; border-top:1px solid #e3e7ed; padding-top:14px;
    font-size:12px; color:#8791a1; display:flex; justify-content:space-between; gap:16px; }
</style></head><body>
  <div class="sheet">
    <div class="band">
      <div class="brand">
        <span class="logo">${GOY_LOGO}</span>
        <span class="name">GoyOne<small>نظام إدارة الأعمال المتكامل</small></span>
      </div>
      <div class="doc">
        <h1>فاتورة اشتراك</h1>
        <div class="num">#${inv.number}</div>
        <span class="status">${st.label}</span>
      </div>
    </div>

    <div class="parties">
      <div class="col">
        <div class="lbl">فوترة إلى</div>
        <div class="val">${escapeHtml(ctx.customer)}</div>
      </div>
      <div class="col">
        <div class="lbl">صادرة عن</div>
        <div class="val">GoyOne</div>
        <div class="sub">billing@goyone.com</div>
      </div>
      <div class="col">
        <div class="lbl">تاريخ الإصدار</div>
        <div class="val">${fmt(inv.issuedAt)}</div>
        <div class="lbl" style="margin-top:8px">تاريخ الاستحقاق</div>
        <div class="val">${fmt(inv.dueAt)}</div>
      </div>
    </div>

    <table>
      <thead><tr><th>البند</th><th class="amt">المبلغ</th></tr></thead>
      <tbody><tr><td>${escapeHtml(desc)}</td><td class="amt">${amount}</td></tr></tbody>
    </table>

    <div class="totals"><div class="box">
      <div class="r"><span>الإجمالي الفرعيّ</span><span class="n">${amount}</span></div>
      <div class="r"><span>الضريبة</span><span class="n">0 ${cur}</span></div>
      <div class="r grand"><span>الإجمالي</span><span class="n">${amount}</span></div>
      ${inv.paidAt ? `<div class="paid-stamp">مدفوعة · ${fmt(inv.paidAt)}</div>` : ''}
    </div></div>

    <div class="foot">
      <span>فاتورةٌ صادرةٌ عن GoyOne لخدمات الاشتراك.</span>
      <span>goyone.com · billing@goyone.com</span>
    </div>
  </div>
</body></html>`;
}

/* طباعةٌ عبر إطارٍ خفيٍّ — لا يحجبه مانعُ النوافذ، ويُحفظ PDF بنقرة. */
function printHtml(html: string): void {
  const frame = document.createElement('iframe');
  frame.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
  document.body.appendChild(frame);
  const doc = frame.contentWindow?.document;
  if (!doc) {
    frame.remove();
    return;
  }
  doc.open();
  doc.write(html);
  doc.close();
  setTimeout(() => {
    frame.contentWindow?.focus();
    frame.contentWindow?.print();
    setTimeout(() => frame.remove(), 1500);
  }, 300);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);
}
